import {LinkOutlined} from '@mui/icons-material';
import {Box, Typography} from '@mui/material';
import {FC} from 'react';
import {FormattedMessage} from 'react-intl';
import {Link} from 'react-router-dom';
import {
    badgeSx,
    cardSx,
    headerSx,
    rowBadgeSx,
    rowEmptySx,
    rowLabelSx,
    rowNoteSx,
    rowPlainSx,
    rowSx,
    rowValueSx,
    rowsSx,
    titleSx,
} from './UserLinkedRail.sx';
import {UserLinkedRailProps} from './UserLinkedRail.types';

/**
 * What this person is connected to.
 *
 * This replaces a column that held two cards and, when the person had neither a charger nor a
 * contract, held nothing at all — a third of the page's width, empty, with no sign that anything was
 * meant to be there. Every row is always present: an unlinked one keeps its label, its icon and its
 * height and says plainly that there is nothing, which is itself an answer a support agent needs.
 */
export const UserLinkedRail: FC<UserLinkedRailProps> = ({rows}) => (
    <Box sx={cardSx}>
        <Box sx={headerSx}>
            <Box sx={badgeSx}>
                <LinkOutlined/>
            </Box>
            <Typography sx={titleSx}>
                <FormattedMessage id="linked"/>
            </Typography>
        </Box>

        <Box sx={rowsSx}>
            {rows.map((row) => {
                const linked = Boolean(row.value);

                return (
                    <Box
                        key={row.label}
                        sx={rowSx}
                    >
                        <Box sx={rowBadgeSx(linked, row.accent)}>{row.icon}</Box>
                        <Box sx={{minWidth: 0}}>
                            <Typography sx={rowLabelSx}>
                                <FormattedMessage id={row.label}/>
                            </Typography>
                            {!linked && (
                                <Typography sx={rowEmptySx}>
                                    <FormattedMessage id={row.emptyLabel ?? 'nothingLinked'}/>
                                </Typography>
                            )}
                            {linked && row.to && (
                                <Typography
                                    component={Link}
                                    to={row.to}
                                    sx={rowValueSx(row.accent)}
                                >
                                    {row.value}
                                </Typography>
                            )}
                            {linked && !row.to && (
                                <Typography sx={rowPlainSx}>
                                    {row.value}
                                    {row.note && <Box component="span" sx={rowNoteSx}>{` · ${row.note}`}</Box>}
                                </Typography>
                            )}
                            {linked && row.to && row.note && (
                                <Typography sx={rowNoteSx}>{row.note}</Typography>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    </Box>
);
